import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  Timestamp,
  increment,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { startFarkleGame } from './service';
import { FarkleStateDocument } from './logic';

export type FarklePlayerStatus = 'waiting' | 'playing' | 'cooldown' | 'left';

export interface FarklePlayerRecord {
  id: string;
  name: string;
  status: FarklePlayerStatus;
  joinedAt: number;
  currentGameId: string | null;
  seatIndex: number | null;
  gamesPlayed: number;
  lastUpdatedAt?: number;
  controlAction?: FarklePlayerControlAction | null;
  controlAckNonce?: number | null;
  controlAckAt?: number | null;
}

export type FarklePlayerActionType = 'roll' | 'select' | 'bank';

export interface FarklePlayerControlAction {
  type: FarklePlayerActionType;
  nonce: number;
  indices?: number[];
  issuedAt?: number;
  power?: number;
}

const playersCollection = (partyId: string) => collection(db, 'parties', partyId, 'farkle_players');
const playerDoc = (partyId: string, playerId: string) => doc(playersCollection(partyId), playerId);

const timestampToMillis = (value: Timestamp | number | undefined) => {
  if (!value) return Date.now();
  if (typeof value === 'number') return value;
  return value.toMillis();
};

export const upsertFarklePlayer = async (
  partyId: string,
  playerId: string,
  name: string,
  status: FarklePlayerStatus = 'waiting'
): Promise<void> => {
  const ref = playerDoc(partyId, playerId);
  const snap = await getDoc(ref);
  const payload: Record<string, unknown> = {
    name: name.trim(),
    status,
    currentGameId: null,
    seatIndex: null,
    lastUpdatedAt: serverTimestamp(),
  };
  if (!snap.exists()) {
    payload.joinedAt = serverTimestamp();
    payload.gamesPlayed = 0;
  }
  await setDoc(ref, payload, { merge: true });
};

export const removeFarklePlayer = async (partyId: string, playerId: string): Promise<void> => {
  await deleteDoc(playerDoc(partyId, playerId));
};

export const updateFarklePlayerStatus = async (
  partyId: string,
  playerId: string,
  status: FarklePlayerStatus
): Promise<void> => {
  await updateDoc(playerDoc(partyId, playerId), {
    status,
    lastUpdatedAt: serverTimestamp(),
    ...(status === 'waiting' ? { currentGameId: null, seatIndex: null } : {}),
  });
};

export const requeueFinishedPlayers = async (
  partyId: string,
  playerIds: string[]
): Promise<void> => {
  await Promise.all(
    playerIds.map((id) =>
      updateDoc(playerDoc(partyId, id), {
        status: 'waiting',
        currentGameId: null,
        seatIndex: null,
        lastUpdatedAt: serverTimestamp(),
      })
    )
  );
};

export const subscribeToFarklePlayers = (
  partyId: string,
  callback: (players: FarklePlayerRecord[]) => void,
  constraints: QueryConstraint[] = []
): (() => void) => {
  const baseRef = playersCollection(partyId);
  const q = constraints.length ? query(baseRef, ...constraints) : baseRef;
  return onSnapshot(q, (snap) => {
    const players: FarklePlayerRecord[] = snap.docs.map((docSnap) => {
      const data = docSnap.data() as any;
      return {
        id: docSnap.id,
        name: data.name ?? 'Player',
        status: (data.status ?? 'waiting') as FarklePlayerStatus,
        joinedAt: timestampToMillis(data.joinedAt),
        currentGameId: data.currentGameId ?? null,
        seatIndex: typeof data.seatIndex === 'number' ? data.seatIndex : null,
        gamesPlayed: data.gamesPlayed ?? 0,
        lastUpdatedAt: timestampToMillis(data.lastUpdatedAt),
        controlAction: data.controlAction ?? null,
        controlAckNonce: typeof data.controlAckNonce === 'number' ? data.controlAckNonce : null,
        controlAckAt: timestampToMillis(data.controlAckAt),
      };
    });
    players.sort((a, b) => a.joinedAt - b.joinedAt);
    callback(players);
  });
};

export const sendFarklePlayerAction = async (
  partyId: string,
  playerId: string,
  action: FarklePlayerControlAction
): Promise<void> => {
  await updateDoc(playerDoc(partyId, playerId), {
    controlAction: {
      ...action,
      issuedAt: serverTimestamp(),
    },
  });
};

export const acknowledgeFarklePlayerAction = async (
  partyId: string,
  playerId: string,
  nonce: number
): Promise<void> => {
  await updateDoc(playerDoc(partyId, playerId), {
    controlAction: deleteField(),
    controlAckNonce: nonce,
    controlAckAt: serverTimestamp(),
  });
};

export const startCrowdFarkleGame = async (
  partyId: string,
  options?: { maxPlayers?: number }
): Promise<{ doc: FarkleStateDocument; playerIds: string[] }> => {
  const maxPlayers = options?.maxPlayers ?? 6;
  const waitingSnap = await getDocs(
    query(playersCollection(partyId), where('status', '==', 'waiting'), orderBy('joinedAt', 'asc'), limit(maxPlayers))
  );
  if (waitingSnap.empty) {
    throw new Error('Add at least one player before starting.');
  }
  const selected = waitingSnap.docs.map((docSnap, index) => ({
    id: docSnap.id,
    name: (docSnap.data() as any).name ?? `Player ${index + 1}`,
  }));
  const docData = await startFarkleGame(
    partyId,
    selected.map((p) => p.name)
  );
  await Promise.all(
    waitingSnap.docs.map((docSnap, index) =>
      updateDoc(docSnap.ref, {
        status: 'playing',
        currentGameId: docData.gameId,
        seatIndex: index,
        lastUpdatedAt: serverTimestamp(),
        gamesPlayed: increment(1),
      })
    )
  );
  return { doc: docData, playerIds: selected.map((p) => p.id) };
};

export const completeCrowdFarkleGame = async (
  partyId: string,
  gameId: string,
  options?: { requeuePlayers?: boolean }
): Promise<void> => {
  const playingSnap = await getDocs(
    query(playersCollection(partyId), where('currentGameId', '==', gameId))
  );
  const requeue = options?.requeuePlayers ?? false;
  await Promise.all(
    playingSnap.docs.map((docSnap) =>
      updateDoc(docSnap.ref, {
        status: requeue ? 'waiting' : 'cooldown',
        currentGameId: null,
        seatIndex: null,
        lastUpdatedAt: serverTimestamp(),
      })
    )
  );
};

export const renameFarklePlayer = async (partyId: string, playerId: string, name: string): Promise<void> => {
  await updateDoc(playerDoc(partyId, playerId), {
    name: name.trim(),
    lastUpdatedAt: serverTimestamp(),
  });
};

export const leaveFarkleQueue = async (partyId: string, playerId: string): Promise<void> => {
  await updateDoc(playerDoc(partyId, playerId), {
    status: 'left',
    currentGameId: null,
    seatIndex: null,
    lastUpdatedAt: serverTimestamp(),
  });
};
